import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { AllCommunityModule, ModuleRegistry, provideGlobalGridOptions } from "ag-grid-community";
import { getColumnState, setColumnState } from "../../core/api/settings";
ModuleRegistry.registerModules([AllCommunityModule]);
provideGlobalGridOptions({ theme: "legacy" });

export default function CustomTable({
  columns,
  data,
  totalAggregatedData,
  loading,
  storageKey,
  onRowClick,
  onScrollEnd,
  hasMore = false,
  loadingMore = false,
}) {
  const gridRef = useRef(null);
  const [gridApi, setGridApi] = useState(null);
  const saveTimeoutRef = useRef(null);

  const columnDefs = useMemo(
    () =>
      columns.map((col) => ({
        headerName: col.header,
        field: col.accessor,
        sortable: true,
        filter: true,
        resizable: true,
        valueFormatter: (params) => {
          const value = params.value;
          if (value === null || value === undefined) return "";
          if (typeof value === "number") {
            return Number.isInteger(value)
              ? value.toLocaleString()
              : value.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                });
          }
          return value;
        },
        pinnedBottomRowData: { totalAggregatedData },
      })),
    [columns, totalAggregatedData]
  );

  const totalRow = useMemo(() => {
    if (!totalAggregatedData) return null;
    return {
      key: "total",
      [columns[0].accessor]: "Total",
      ...Object.fromEntries(
        columns
          .slice(1)
          .map((col) => [col.accessor, col.total_key ? totalAggregatedData[col.total_key] : ""])
      ),
    };
  }, [columns, totalAggregatedData]);

  const restoreColumnState = useCallback(async () => {
    if (!gridApi || !storageKey) return;

    // try {
    //   const response = await getColumnState(storageKey);
    //   if (response.data?.column_state) {
    //     gridApi.applyColumnState({
    //       state: response.data.column_state,
    //       applyOrder: true,
    //     });
    //   }
    // } catch (error) {
    //   console.error("Error restoring column state:", error);
    // }
  }, [gridApi, storageKey]);

  const saveColumnState = useCallback(() => {
    if (!gridApi || !storageKey) return;

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    saveTimeoutRef.current = setTimeout(async () => {
      const columnState = gridApi.getColumnState();
      try {
        await setColumnState(storageKey, columnState);
      } catch (error) {
        console.error("Error saving column state:", error);
      }
    }, 500);
  }, [gridApi, storageKey]);

  useEffect(() => {
    if (gridApi) {
      restoreColumnState();
    }
  }, [gridApi, restoreColumnState]);

  const onGridReady = useCallback((params) => {
    setGridApi(params.api);
  }, []);

  const onColumnStateChanged = useCallback(() => {
    saveColumnState();
  }, [saveColumnState]);

  // Store the last scroll position to prevent multiple triggers
  const lastScrollPositionRef = useRef(0);
  const scrollThrottleTimerRef = useRef(null);

  // Handle scroll events for lazy loading
  const onBodyScroll = useCallback(
    (params) => {
      if (!onScrollEnd || !hasMore || loadingMore) return;

      const { api } = params;
      if (!api) return;

      // Throttle scroll events to prevent multiple triggers
      if (scrollThrottleTimerRef.current) return;

      scrollThrottleTimerRef.current = setTimeout(() => {
        // Get grid viewport and total height information
        const gridBodyElement = document.querySelector(".ag-body-viewport");
        if (!gridBodyElement) {
          scrollThrottleTimerRef.current = null;
          return;
        }

        const scrollPosition = gridBodyElement.scrollTop;
        const totalHeight = gridBodyElement.scrollHeight;
        const viewportHeight = gridBodyElement.clientHeight;

        // Don't trigger if we're scrolling up
        if (scrollPosition < lastScrollPositionRef.current) {
          lastScrollPositionRef.current = scrollPosition;
          scrollThrottleTimerRef.current = null;
          return;
        }

        // Update last scroll position
        lastScrollPositionRef.current = scrollPosition;

        // Calculate how close we are to the bottom (within 150px or 25% of viewport height)
        const threshold = Math.min(150, viewportHeight * 0.25);
        const nearBottom = totalHeight - (scrollPosition + viewportHeight) < threshold;

        // If we're near the bottom and there are more rows to load
        if (nearBottom && api.getDisplayedRowCount() > 0) {
          onScrollEnd();
        }

        scrollThrottleTimerRef.current = null;
      }, 200); // 200ms throttle
    },
    [onScrollEnd, hasMore, loadingMore]
  );

  // Clean up the throttle timer on unmount
  useEffect(() => {
    return () => {
      if (scrollThrottleTimerRef.current) {
        clearTimeout(scrollThrottleTimerRef.current);
      }
    };
  }, []);

  return (
    <div className="ag-theme-quartz" style={{ height: "450px", width: "100%" }}>
      <AgGridReact
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={Array.isArray(data) ? data : []}
        defaultColDef={{ flex: 1, minWidth: 100 }}
        pagination={false}
        pinnedBottomRowData={totalRow ? [totalRow] : []}
        loading={loading}
        onGridReady={onGridReady}
        onColumnMoved={onColumnStateChanged}
        onColumnPinned={onColumnStateChanged}
        onColumnVisible={onColumnStateChanged}
        onColumnResized={onColumnStateChanged}
        onSortChanged={onColumnStateChanged}
        onFilterChanged={onColumnStateChanged}
        onBodyScroll={onScrollEnd ? onBodyScroll : undefined}
        rowSelection="single"
        onRowClicked={onRowClick ? (params) => onRowClick(params.data) : undefined}
      />
    </div>
  );
}
